import { ChangeEvent, ReactNode, useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Checkbox } from '@Components/FormCheck';
import { Page } from '@Components/Page/Page';
import { Button } from '@Components/Button/Button';
import { FullPageSpinner } from '@Components/Spinner/Spinner';
import { WidgetContainer } from '@Components/WidgetContainer';
import { InfoTooltip } from '@Components/InfoTooltip/InfoTooltip';
import { Dropdown } from '@Components/Dropdown';
import { Background, Size } from '@Components/designConstants';
import { useAppSelector } from '@src/redux';
import { isAdminSelector } from '@src/redux/user';
import { useSetError } from '@src/redux/modals';
import {
  DagStartResult,
  startDag,
  startSingleJob,
  useDagNames,
  useDagTasksWithErrorModal
} from './use-dag-start';
import { getTaskFieldPlaceholder } from './dag-start-utils';
import './DagStart.scss';

export const DagStart = () => {
  const isAdmin = useAppSelector(isAdminSelector);
  const setError = useSetError();
  const { data: dagNames, isLoading, error } = useDagNames();

  useEffect(() => {
    if (error) {
      setError({
        title: "Couldn't load dag names",
        message: `Failed to load dag names: ${error.message}`,
        reportError: error
      });
    }
  }, [error, setError]);

  return (
    <Page title='Dag Start' pageType='Dag Start' minHeight='600px' isLoading={isLoading}>
      <DagStartBody dagNames={dagNames} isLoading={isLoading} isAdmin={isAdmin} />
    </Page>
  );
};

interface DagStartBodyProps {
  dagNames?: string[];
  isLoading: boolean;
  isAdmin: boolean;
}

const DagStartBody = ({ dagNames = [], isLoading, isAdmin }: DagStartBodyProps) => {
  if (isLoading) return <FullPageSpinner />;

  return (
    <div className='dag-start-page'>
      <StartDagSection dagNames={dagNames} />
      {isAdmin && <StartSingleJobSection dagNames={dagNames} />}
    </div>
  );
};

interface FieldLabelProps {
  label: string;
  htmlFor?: string;
  children: ReactNode;
}

const FieldLabel = ({ label, htmlFor, children }: FieldLabelProps) => (
  <div className='field-label'>
    <Form.Label htmlFor={htmlFor}>{label}</Form.Label>
    <InfoTooltip size={Size.MD}>{children}</InfoTooltip>
  </div>
);

interface DagNameSelectProps {
  dagNames: string[];
  value: string;
  onSelect: (dagName: string) => void;
}

const DagNameSelect = ({ dagNames, value, onSelect }: DagNameSelectProps) => (
  <Form.Group>
    <Form.Label>Dag Name</Form.Label>
    <div className='dag-name-select'>
      <Dropdown
        size={Size.LG}
        searchable
        clearable
        options={dagNames}
        value={value || null}
        onChange={next => onSelect(next ?? '')}
        placeholder='Select a dag...'
      />
    </div>
  </Form.Group>
);

interface OverrideCheckboxProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const OverrideCheckbox = ({ id, checked, onChange }: OverrideCheckboxProps) => (
  <Checkbox
    id={id}
    label='Override? (optional)'
    checked={checked}
    onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
  />
);

interface DagStartOutputProps {
  result?: DagStartResult;
}

const DagStartOutput = ({ result }: DagStartOutputProps) => {
  if (!result) return null;

  return (
    <div className='lambda-output' data-testid='lambda-output'>
      <hr />
      <h5>Lambda Output</h5>
      <div>Success: {String(result.success)}</div>
      <div>Message:</div>
      <div>{result.message}</div>
    </div>
  );
};

interface DagStartSectionProps {
  dagNames: string[];
}

interface TaskDropdownProps {
  className: string;
  options: string[];
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder: string;
}

const TaskDropdown = ({
  className,
  options,
  value,
  onChange,
  disabled,
  placeholder
}: TaskDropdownProps) => (
  <div className={className}>
    <Dropdown
      size={Size.LG}
      searchable
      clearable
      options={options}
      value={value || null}
      onChange={next => onChange(next ?? '')}
      disabled={disabled}
      placeholder={placeholder}
    />
  </div>
);

const StartDagSection = ({ dagNames }: DagStartSectionProps) => {
  const setError = useSetError();
  const [selectedDag, setSelectedDag] = useState('');
  const [selectedTask, setSelectedTask] = useState('');
  const [selectedTaskName, setSelectedTaskName] = useState('');
  const [override, setOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<DagStartResult>();

  const { data: dagTasks, isFetching: isFetchingTasks } = useDagTasksWithErrorModal(selectedDag);
  const tasksDisabled = !selectedDag || isFetchingTasks;

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const response = await startDag({
        dag: selectedDag,
        start_task: selectedTask,
        start_task_name: selectedTaskName,
        override,
        retries: 0
      });
      setResult(response);
    } catch (e) {
      setError({ message: `Failed to start dag: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <WidgetContainer header='Start Dag' background={Background.DARK} dataTestId='start-dag-widget'>
      <div className='dag-start-section'>
        <div className='inputs'>
          <DagNameSelect
            dagNames={dagNames}
            value={selectedDag}
            onSelect={dagName => {
              setSelectedDag(dagName);
              setSelectedTask('');
              setSelectedTaskName('');
            }}
          />
          <Form.Group>
            <FieldLabel label='Task Select (optional)'>
              Restarts the dag at the first task of this type (e.g. WAITKPI), skipping everything
              upstream. Leave blank to run the whole dag from the start.
            </FieldLabel>
            <TaskDropdown
              className='task-select'
              options={dagTasks?.tasks ?? []}
              value={selectedTask}
              onChange={setSelectedTask}
              disabled={tasksDisabled}
              placeholder={getTaskFieldPlaceholder(
                selectedDag,
                isFetchingTasks,
                'Select a task...'
              )}
            />
          </Form.Group>
          <Form.Group>
            <FieldLabel label='Task Name Select (optional)'>
              Restarts the dag at this exact step — more precise than Task Select. Leave blank to
              run the whole dag from the start.
            </FieldLabel>
            <TaskDropdown
              className='task-name-select'
              options={dagTasks?.taskNames ?? []}
              value={selectedTaskName}
              onChange={setSelectedTaskName}
              disabled={tasksDisabled}
              placeholder={getTaskFieldPlaceholder(
                selectedDag,
                isFetchingTasks,
                'Select a task name...'
              )}
            />
          </Form.Group>
          <OverrideCheckbox id='dag-start-override' checked={override} onChange={setOverride} />
          <div className='submit-button'>
            <Button
              size={Size.LG}
              data-testid='start-dag-submit'
              loading={saving}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
        <DagStartOutput result={result} />
      </div>
    </WidgetContainer>
  );
};

const StartSingleJobSection = ({ dagNames }: DagStartSectionProps) => {
  const setError = useSetError();
  const [selectedDag, setSelectedDag] = useState('');
  const [selectedTaskName, setSelectedTaskName] = useState('');
  const [select, setSelect] = useState('');
  const [exclude, setExclude] = useState('');
  const [override, setOverride] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<DagStartResult>();

  const { data: dagTasks, isFetching: isFetchingTasks } = useDagTasksWithErrorModal(selectedDag);
  const tasksDisabled = !selectedDag || isFetchingTasks;

  const handleSubmit = async () => {
    try {
      setSaving(true);
      const response = await startSingleJob({
        dag: selectedDag,
        task_name: selectedTaskName,
        select,
        exclude,
        override,
        retries: 0
      });
      setResult(response);
    } catch (e) {
      setError({ message: `Failed to start dag: ${e.message}`, reportError: e });
    } finally {
      setSaving(false);
    }
  };

  return (
    <WidgetContainer
      header='Start Single Job'
      background={Background.DARK}
      dataTestId='start-single-job-widget'
    >
      <div className='dag-start-section'>
        <div className='inputs'>
          <DagNameSelect
            dagNames={dagNames}
            value={selectedDag}
            onSelect={dagName => {
              setSelectedDag(dagName);
              setSelectedTaskName('');
            }}
          />
          <Form.Group>
            <FieldLabel label='Task Name Select'>
              The exact step to run as a standalone job, without running the rest of the dag.
            </FieldLabel>
            <TaskDropdown
              className='task-name-select'
              options={dagTasks?.taskNames ?? []}
              value={selectedTaskName}
              onChange={setSelectedTaskName}
              disabled={tasksDisabled}
              placeholder={getTaskFieldPlaceholder(
                selectedDag,
                isFetchingTasks,
                'Select a task name...'
              )}
            />
          </Form.Group>
          <Form.Group className='dag-start-row'>
            <div className='dag-start-left'>
              <FieldLabel label='Select (Optional)' htmlFor='dag-start-select'>
                Only applies to dbt tasks (e.g. MOBIUS-DBT) — passed through as dbt&apos;s{' '}
                <code>--select</code> model filter. Ignored for any other task type.
              </FieldLabel>
              <Form.Control
                id='dag-start-select'
                type='text'
                value={select}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSelect(e.target.value)}
              />
            </div>
            <div className='dag-start-right'>
              <FieldLabel label='Exclude (Optional)' htmlFor='dag-start-exclude'>
                Only applies to dbt tasks (e.g. MOBIUS-DBT) — passed through as dbt&apos;s{' '}
                <code>--exclude</code> model filter. Ignored for any other task type.
              </FieldLabel>
              <Form.Control
                id='dag-start-exclude'
                type='text'
                value={exclude}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setExclude(e.target.value)}
              />
            </div>
          </Form.Group>
          <OverrideCheckbox
            id='dag-start-single-job-override'
            checked={override}
            onChange={setOverride}
          />
          <div className='submit-button'>
            <Button
              size={Size.LG}
              data-testid='start-single-job-submit'
              loading={saving}
              onClick={handleSubmit}
            >
              Submit
            </Button>
          </div>
        </div>
        <DagStartOutput result={result} />
      </div>
    </WidgetContainer>
  );
};
